import React, { useEffect, useState } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";
import Modal from "../../common/CustomModal";
import { useGalleryContext } from "../../../context/GalleryContext";
import { NoImageFeedback } from "./NoImageFeedback";
import { weightSorter } from "../../../helpers/sortUtils";
type Image = {
  url: string;
  weight?: number;
};
type Group = {
  groupId: string | null;
  images: Image[];
  desc: string;
  weight?: number | null;
};
const WorkCompleted = () => {
  const { t } = useTranslation("gallery");
  const { workCompleted } = useGalleryContext();

  const workImages = React.useMemo(() => {
    const grouped = new Map<string, Group>();

    workCompleted?.forEach((item) => {
      if (item.groupId) {
        if (!grouped.has(item.groupId)) {
          grouped.set(item.groupId, {
            groupId: item.groupId,
            images: [],
            desc: item.description,
            weight: item.group?.groupWeight,
          });
        }
        const groupImages: Image[] = grouped.get(item.groupId)!.images;
        grouped.get(item.groupId)!.images = weightSorter([
          ...groupImages,
          { url: item.url, weight: item.weight },
        ]);
      } else {
        grouped.set(`standalone-${item.id}`, {
          groupId: null,
          images: [{ url: item.url, weight: item.weight }],
          desc: item.description,
          weight: item.weight,
        });
      }
    });

    return weightSorter(Array.from(grouped.values()));
  }, [workCompleted]);

  const [currentImageIndexes, setCurrentImageIndexes] = useState<{
    [key: number]: number;
  }>({});

  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    groupIndex: number;
    imageIndex: number;
  } | null>(null);

  const [imageLoading, setImageLoading] = useState<{ [key: number]: boolean }>(
    {},
  );

  useEffect(() => {
    if (workImages.length > 0) {
      setCurrentImageIndexes(
        workImages.reduce((acc, _, idx) => ({ ...acc, [idx]: 0 }), {}),
      );
    }
  }, [workImages]);

  useEffect(() => {
    Object.entries(currentImageIndexes).forEach(([idx, index]) => {
      const group = workImages[+idx];
      if (!group) return;

      const preload = (i: number) => {
        const url = group.images[i]?.url;
        if (url) {
          const img = new Image();
          img.src = url;
        }
      };

      preload(index + 1);
      preload(index - 1);
    });
  }, [currentImageIndexes, workImages]);

  const handleImageChange = (index: number, direction: "next" | "prev") => {
    setCurrentImageIndexes((prev) => ({
      ...prev,
      [index]:
        direction === "next"
          ? Math.min(prev[index] + 1, workImages[index].images.length - 1)
          : Math.max(prev[index] - 1, 0),
    }));
  };

  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && selectedImage.imageIndex > 0) {
        const newIndex = selectedImage.imageIndex - 1;
        setSelectedImage({
          url: workImages[selectedImage.groupIndex].images[newIndex].url,
          groupIndex: selectedImage.groupIndex,
          imageIndex: newIndex,
        });
      }

      if (e.key === "ArrowRight") {
        const group = workImages[selectedImage.groupIndex];
        if (selectedImage.imageIndex < group.images.length - 1) {
          const newIndex = selectedImage.imageIndex + 1;
          setSelectedImage({
            url: group.images[newIndex].url,
            groupIndex: selectedImage.groupIndex,
            imageIndex: newIndex,
          });
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage, workImages]);

  if (!workImages || workImages.length === 0) {
    return (
      <div
        className="
          min-h-[200px] flex items-center justify-center
          p-4
        "
      >
        <NoImageFeedback />
      </div>
    );
  }

  return (
    <section className="w-full">
      <div className=" maxContainer">
        <div className="flex flex-col items-center w-full">
          <div className="grid w-full grid-cols-2 mt-12 md:grid-cols-3 ">
            {workImages.map((work, idx) => {
              const currentIndex = currentImageIndexes[idx] ?? 0;
              const currentImage = work.images[currentIndex];
              if (!currentImage) return null; // safety check

              return (
                <div
                  key={work.groupId ?? `standalone-${idx}`}
                  className="relative cursor-pointer ss:h-[274px] border w-full overflow-hidden group"
                  onClick={() =>
                    setSelectedImage({
                      url: currentImage.url,
                      groupIndex: idx,
                      imageIndex: currentIndex,
                    })
                  }
                >
                  <div
                    className="absolute inset-0 transition-transform duration-300 ease-in-out transform bg-center bg-cover group-hover:scale-105"
                    style={{
                      backgroundImage: `url(${currentImage.url})`,
                    }}
                  />
                  {imageLoading[idx] && (
                    <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
                  )}

                  <img
                    src={currentImage.url}
                    alt="gallery-img"
                    className={`object-cover w-full h-full transition-opacity duration-500 ease-in-out ${
                      imageLoading[idx] ? "opacity-0" : "opacity-100"
                    }`}
                    loading="lazy"
                    decoding="async"
                    onLoad={() =>
                      setImageLoading((prev) => ({ ...prev, [idx]: false }))
                    }
                  />

                  <div className="absolute inset-0 flex items-center justify-between transition-opacity opacity-0 ss:px-4 group-hover:opacity-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageChange(idx, "prev");
                      }}
                      className={`bg-white rounded-full p-1 ss:p-2 shadow-md hover:bg-gray-200 transition-all ${
                        currentIndex === 0 ? "opacity-50 " : ""
                      }`}
                      disabled={currentIndex === 0}
                    >
                      <ChevronLeftIcon />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleImageChange(idx, "next");
                      }}
                      className={`bg-white rounded-full p-1 ss:p-2 shadow-md hover:bg-gray-200 transition-all ${
                        currentIndex === work.images.length - 1
                          ? "opacity-50 "
                          : ""
                      }`}
                      disabled={currentIndex === work.images.length - 1}
                    >
                      <ChevronRightIcon />
                    </button>
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between px-5 py-4 transition-all duration-700 ease-in-out translate-y-5 shadow-lg opacity-0 bg-white/80 backdrop-blur-md group-hover:opacity-100 group-hover:translate-y-0">
                    <span className="text-[10px] ss:text-[14px] font-medium text-[#000000bd]">
                      {t(work.desc)}
                    </span>
                    <div className="text-[12px] text-greyText">
                      {currentIndex + 1}/{work.images.length}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Modal */}
      <Modal
        isOpen={!!selectedImage}
        onClose={() => {
          if (selectedImage) {
            setCurrentImageIndexes((prev) => ({
              ...prev,
              [selectedImage.groupIndex]: selectedImage.imageIndex,
            }));
          }
          setSelectedImage(null);
        }}
        size="max-content"
      >
        {selectedImage && (
          <div className="relative flex items-center justify-center w-full">
            <button
              onClick={() => {
                if (selectedImage.imageIndex > 0) {
                  const newIndex = selectedImage.imageIndex - 1;
                  setSelectedImage({
                    url: workImages[selectedImage.groupIndex].images[newIndex]
                      .url,
                    groupIndex: selectedImage.groupIndex,
                    imageIndex: newIndex,
                  });
                }
              }}
              disabled={selectedImage.imageIndex === 0}
              className={`absolute left-0 z-10 p-2 m-2 bg-white rounded-full shadow-md hover:bg-gray-200 ${
                selectedImage.imageIndex === 0
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronLeftIcon />
            </button>

            <img
              loading="lazy"
              decoding="async"
              src={
                workImages[selectedImage.groupIndex].images[
                  selectedImage.imageIndex
                ].url
              }
              className="object-contain max-h-[80vh] w-auto max-w-[90vw] mx-auto"
              alt="completed work"
            />

            <button
              onClick={() => {
                const group = workImages[selectedImage.groupIndex];
                if (selectedImage.imageIndex < group.images.length - 1) {
                  const newIndex = selectedImage.imageIndex + 1;
                  setSelectedImage({
                    url: group.images[newIndex].url,
                    groupIndex: selectedImage.groupIndex,
                    imageIndex: newIndex,
                  });
                }
              }}
              disabled={
                selectedImage.imageIndex ===
                workImages[selectedImage.groupIndex].images.length - 1
              }
              className={`absolute right-0 z-10 p-2 m-2 bg-white rounded-full shadow-md hover:bg-gray-200 ${
                selectedImage.imageIndex ===
                workImages[selectedImage.groupIndex].images.length - 1
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <ChevronRightIcon />
            </button>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default WorkCompleted;
